/**
 * Deferred loading for hero background videos (header content element).
 *
 * The server renders a responsive poster image plus a <video preload="none" data-src="…">.
 * The video source is only attached after the page has loaded, so the poster is the
 * LCP element and the video no longer competes with CSS/JS/fonts for bandwidth.
 */
export type HeroVideoEnv = { isMobile: boolean; reducedMotion: boolean };

export function resolveVideoSource(video: HTMLVideoElement, env: HeroVideoEnv): string | null {
	if (env.reducedMotion) return null;
	const mobile = video.dataset.srcMobile;
	const desktop = video.dataset.src;
	if (env.isMobile && mobile) return mobile;
	return desktop || null;
}

export class HeroVideoInitializer {
	init(): void {
		const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('video[data-src]'));
		if (!videos.length) return;

		const start = () => this.whenIdle(() => videos.forEach((video) => this.load(video)));
		if (document.readyState === 'complete') start();
		else window.addEventListener('load', start, { once: true });
	}

	private whenIdle(callback: () => void): void {
		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(() => callback(), { timeout: 2000 });
		} else {
			window.setTimeout(callback, 200);
		}
	}

	private load(video: HTMLVideoElement): void {
		const src = resolveVideoSource(video, {
			isMobile: window.matchMedia('(max-width: 767px)').matches,
			reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
		});
		if (!src) return;

		const container = video.closest<HTMLElement>('.image-container');
		video.addEventListener('playing', () => container?.classList.add('is-playing'), { once: true });
		video.src = src;
		video.play().catch(() => {
			// Autoplay may be blocked by the browser; the poster simply stays visible.
		});
	}
}
